import { AI_PAGE_ID } from '@flowus/common/chat-ai/hooks/types';
import { useAIChatIpc } from '@flowus/common/chat-ai/hooks/use-ai-chat-ipc';
import { AIChatIndex } from '@flowus/common/chat-ai/pages';
import { cx } from '@flowus/common/cx';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { PRODUCT_AI_TITLE } from 'src/const/title';
import { useAiChat } from 'src/hooks/ai/use-ai-chat';
import { useAutoSwitchSpace, useCurrentSpace } from 'src/hooks/space';
import { setBrowserTitle } from 'src/utils/emoji-favicon';
import { TocCollapseButton } from './main/aside/toc-trigger';
import { isMobile } from 'react-device-detect';
import { Icon } from '@flowus/common/icon-svg/pc-icon';
import { $appUiStateCache, setAppUiState } from 'src/services/app';
import { AiHistoryList } from './main/aside/ai-history-list';
import { SearchParams, ViewPath } from 'src/utils';
import { useHistory } from 'react-router-dom';
import { switchSpaceViewIdBySpaceId } from 'src/hooks/user/switch-space-view-id';
import type { BlockDTO } from '@next-space/fe-api-idl';
import { dispatch } from 'src/redux/store';
import { blocksActions } from 'src/redux/reducers/blocks';
import type { NextBlock } from 'src/redux/types';

export const ChatPage: FC<{ id?: string }> = (props) => {
  useAIChatIpc();

  const { id } = props;
  const {
    context,
    updateContext,
    navigateToChat,
    onSaveArticle,
    onRenderBlockPreview,
    renderPageIcon,
    openUpgrade,
    onLoadConversation,
  } = useAiChat();
  const history = useHistory();
  const updateBlocks = useCallback((blocks?: Record<string, BlockDTO>) => {
    blocks && dispatch(blocksActions.update({ blocks: blocks as Record<string, NextBlock> }));
  }, []);

  const navbar = useCallback(() => {
    if (isMobile) {
      return (
        <div className="flex items-center justify-between px-6 py-2.5">
          <div
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setAppUiState({ $showAiHistory: !$appUiStateCache.$showAiHistory });
            }}
          >
            <Icon name="IcBannerMore" size="middle" />
          </div>
          <div
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              history.push(ViewPath.chat);
            }}
          >
            <Icon name="IcAdd" size="middle" />
          </div>
        </div>
      );
    }

    return (
      <TocCollapseButton
        size="small"
        visibilityCallback={(isCollapsed) => isCollapsed}
        className={cx(
          'opacity-100',
          context.conversationId === AI_PAGE_ID && 'fixed top-4 left-4',
          context.conversationId !== AI_PAGE_ID && 'pt-2'
        )}
      />
    );
  }, [context.conversationId, history]);

  const currentSpace = useCurrentSpace();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const spaceId = params.get(SearchParams.spaceId) ?? undefined;
    if (spaceId) {
      switchSpaceViewIdBySpaceId(spaceId);
    }
  }, []);

  useEffect(() => {
    const conversationId = id || AI_PAGE_ID;
    if (conversationId !== context.conversationId) {
      updateContext({ conversationId });
    }
    const spaceId = currentSpace.uuid;
    if (spaceId !== context.spaceId) {
      updateContext({ spaceId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentSpace.uuid]);

  useEffect(() => {
    void setBrowserTitle({ title: PRODUCT_AI_TITLE });
  }, []);

  return (
    <>
      <AIChatIndex
        navbar={navbar}
        {...context}
        updateBlocks={updateBlocks}
        navigateToChat={navigateToChat}
        updateContext={updateContext}
        onSaveArticle={onSaveArticle}
        renderPageIcon={renderPageIcon}
        openUpgrade={openUpgrade}
        onRenderBlockPreview={onRenderBlockPreview}
        onLoadConversation={onLoadConversation}
      />
      {isMobile && <AiHistoryList />}
    </>
  );
};
